/**
 * Purchase-orders list — KPI strip + status tabs + table + cursor "Load more".
 *
 * Mirrors the bills list shape (KPI tiles, filter tabs, a table with a
 * status pill, cursor pagination) but simpler — no bulk-approve, no
 * step-up, no recurring tab. The status tabs map 1:1 to the `poStatus`
 * enum (collapsing the GRN-side received states into "Confirmed/Closed"
 * groupings shown in the prototype); counts come from `list.counts`.
 *
 * @see prototype/purchases/purchase-orders.html — V1.1 baseline (list)
 * @see packages/api-contract/src/procedures/purchase-orders.ts
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Boxes, ClipboardList, Clock, Plus, Search, Truck } from "lucide-react";
import { Alert, Button, StatusPill, TabsContent, TabsList, TabsRoot, TabsTrigger } from "@flint/ui";
import { DataTableCard, ListPagination, avatarClassFor, initialsFor } from "../../master-list/index.ts";
import { trpc } from "../../../lib/trpc.js";
import { formatDate, paiseToInr, paiseToInrShort } from "../../../lib/format.js";
import { poStatusLabel, poStatusTone, type PoStatus } from "../lib/status.js";

type StatusFilter = "all" | "draft" | "sent" | "confirmed" | "closed" | "cancelled";

const TABS: Array<{ id: StatusFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "draft", label: "Draft" },
  { id: "sent", label: "Sent" },
  { id: "confirmed", label: "Confirmed" },
  { id: "closed", label: "Closed" },
  { id: "cancelled", label: "Cancelled" },
];

/** Server-side `status` filter for a tab, when it maps to a single enum value. */
const SERVER_STATUS: Record<StatusFilter, PoStatus | undefined> = {
  all: undefined,
  draft: "draft",
  sent: "sent",
  confirmed: "confirmed",
  closed: "closed",
  cancelled: "cancelled",
};

export function PoListPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [draftQuery, setDraftQuery] = useState("");
  const [committedQuery, setCommittedQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const serverStatus = SERVER_STATUS[statusFilter];

  // Cursor-stack pagination (the shared list pattern — see vendors list).
  const PAGE_SIZE = 50;
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];
  useEffect(() => {
    setCursorStack([]);
  }, [statusFilter, committedQuery]);

  const list = trpc.purchaseOrders.list.useQuery(
    {
      limit: PAGE_SIZE,
      ...(serverStatus ? { status: serverStatus } : {}),
      ...(committedQuery ? { search: committedQuery } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCommittedQuery(draftQuery.trim());
  };

  const rows = list.data?.items ?? [];
  const counts = list.data?.counts;
  const kpis = list.data?.kpis;

  // Pagination derivations — page index = cursor-stack depth + 1.
  const pageNumber = cursorStack.length + 1;
  const showingFrom = rows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + rows.length;
  const totalRows = counts ? counts[statusFilter] : undefined;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    const next = list.data?.nextCursor;
    if (next) setCursorStack((s) => [...s, next]);
  };

  const stats = computePoStats(rows);

  const lead = list.data
    ? `${counts?.all ?? 0} orders · ${paiseToInrShort(kpis?.openValuePaise ?? "0")} committed · ${kpis?.awaitingConfirmationCount ?? 0} awaiting confirmation`
    : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-bold tracking-tight text-ink">Purchase orders</h1>
          {lead ? <p className="mt-1 text-sm text-muted">{lead}</p> : null}
        </div>
        <Button asChild>
          <Link to="/procurement/purchase-orders/new">
            <Plus size={15} />
            New purchase order
          </Link>
        </Button>
      </div>

      {list.error ? (
        <Alert
          intent="bad"
          title="Couldn't load purchase orders"
          description={list.error.message}
        />
      ) : null}

      {/* KPI strip */}
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile
          tone="sky"
          icon={<ClipboardList size={16} />}
          label="Open orders"
          value={paiseToInrShort(kpis?.openValuePaise ?? "0")}
          meta={`${kpis?.openCount ?? 0} open${kpis?.topVendorName ? ` · top ${kpis.topVendorName}` : ""}`}
        />
        <KpiTile
          tone="muted"
          icon={<Boxes size={16} />}
          label="Drafts"
          value={String(kpis?.draftCount ?? 0)}
          meta="not yet sent"
        />
        <KpiTile
          tone="warn"
          icon={<Clock size={16} />}
          label="Awaiting confirmation"
          value={String(kpis?.awaitingConfirmationCount ?? 0)}
          meta="sent · awaiting vendor"
        />
        <KpiTile
          tone="ok"
          icon={<Truck size={16} />}
          label="Expected this week"
          value={String(kpis?.expectedThisWeekCount ?? 0)}
          meta="delivery due ≤ 7 days"
        />
      </section>

      {/* Status tabs */}
      <TabsRoot value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
        <TabsList aria-label="Purchase order status">
          {TABS.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} count={counts ? counts[tab.id] : undefined}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {/* Each Radix TabsTrigger sets aria-controls to a panel id; the
            filtered table below isn't a Radix panel, so without these the
            references dangle (axe aria-valid-attr-value, critical). Same
            fix as proforma-list-page.tsx. */}
        {TABS.map((tab) => (
          <TabsContent key={tab.id} value={tab.id} forceMount className="hidden" aria-hidden />
        ))}
      </TabsRoot>

      {/* Search toolbar */}
      <form onSubmit={submitSearch} className="flex items-center gap-2 flex-wrap">
        <label className="flex flex-1 max-w-sm items-center gap-2 h-9 rounded-md border border-line bg-paper px-3 text-muted focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100">
          <Search size={14} aria-hidden />
          <input
            ref={searchRef}
            type="search"
            placeholder="Search PO #, vendor…"
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            aria-label="Search purchase orders"
            className="flex-1 min-w-0 border-0 bg-transparent text-sm text-ink placeholder:text-muted outline-none"
          />
        </label>
      </form>

      {/* Table */}
      <DataTableCard
        footer={
          rows.length > 0
            ? [
                { label: "Visible", value: rows.length },
                { label: "Page total", value: paiseToInrShort(stats.sumPaise.toString()) },
                { label: "Avg", value: paiseToInrShort(stats.avgPaise.toString()) },
                ...(stats.largest
                  ? [
                      {
                        label: "Largest",
                        value: paiseToInrShort(stats.largest.paise.toString()),
                        suffix: ` · ${stats.largest.vendor}`,
                      },
                    ]
                  : []),
                ...(stats.dateRange ? [{ label: "Ordered", value: stats.dateRange }] : []),
              ]
            : []
        }
        pagination={
          rows.length > 0 ? (
            <ListPagination
              pageNumber={pageNumber}
              showingFrom={showingFrom}
              showingTo={showingTo}
              {...(typeof totalRows === "number" ? { total: totalRows } : {})}
              sortLabel="sorted by creation order (newest first)"
              hasPrev={cursorStack.length > 0}
              hasNext={!!list.data?.nextCursor}
              onPrev={handlePrev}
              onNext={handleNext}
            />
          ) : undefined
        }
      >
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-3 py-2.5 text-left font-medium">PO #</th>
                <th className="px-3 py-2.5 text-left font-medium">Vendor</th>
                <th className="px-3 py-2.5 text-left font-medium">Order date</th>
                <th className="px-3 py-2.5 text-left font-medium">Expected</th>
                <th className="px-3 py-2.5 text-right font-medium">Total</th>
                <th className="px-3 py-2.5 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-sm text-muted">
                    Loading purchase orders…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center text-sm text-muted">
                    {committedQuery || statusFilter !== "all" ? (
                      committedQuery ? (
                        <>
                          No purchase orders match your search.
                          <br />
                          Try a different PO number or vendor name.
                        </>
                      ) : (
                        <>Nothing in this tab yet.</>
                      )
                    ) : (
                      <>
                        No purchase orders yet.{" "}
                        <Link
                          to="/procurement/purchase-orders/new"
                          className="text-sky-700 hover:underline"
                        >
                          Raise your first order.
                        </Link>
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-t border-line transition-colors hover:bg-raised">
                    <td className="px-3 py-3 font-mono font-semibold text-ink-2">
                      {row.poNumber ? (
                        <Link
                          to="/procurement/purchase-orders/$poId"
                          params={{ poId: row.id }}
                          className="hover:text-sky-700"
                        >
                          {row.poNumber}
                        </Link>
                      ) : (
                        <Link
                          to="/procurement/purchase-orders/$poId"
                          params={{ poId: row.id }}
                          className="hover:underline"
                        >
                          <span className="text-muted">— draft —</span>
                        </Link>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2.5">
                        <span
                          aria-hidden
                          className={`grid h-7 w-7 shrink-0 place-items-center rounded-md text-xs font-bold text-white ${avatarClassFor(row.vendorName)}`}
                        >
                          {initialsFor(row.vendorName)}
                        </span>
                        <div className="min-w-0">
                          <div className="font-medium text-ink truncate">{row.vendorName}</div>
                          {row.vendorCity ? (
                            <div className="text-xs text-muted">{row.vendorCity}</div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 font-mono text-ink-2">{formatDate(row.orderDate)}</td>
                    <td className="px-3 py-3 font-mono text-ink-2">
                      {row.expectedDate ? formatDate(row.expectedDate) : "—"}
                    </td>
                    <td className="px-3 py-3 text-right font-mono font-semibold text-ink">
                      {paiseToInr(row.totalPaise)}
                    </td>
                    <td className="px-3 py-3">
                      <StatusPill tone={poStatusTone(row.status as PoStatus)} size="sm" dot>
                        {poStatusLabel(row.status as PoStatus)}
                      </StatusPill>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
      </DataTableCard>
    </div>
  );
}

/** Visible-set aggregates for the stats footer. */
function computePoStats(rows: Array<{ totalPaise: string; vendorName: string; orderDate: string }>): {
  sumPaise: bigint;
  avgPaise: bigint;
  largest: { paise: bigint; vendor: string } | null;
  dateRange: string | null;
} {
  if (rows.length === 0) {
    return { sumPaise: 0n, avgPaise: 0n, largest: null, dateRange: null };
  }
  let sum = 0n;
  let largest: { paise: bigint; vendor: string } | null = null;
  let min = rows[0]!.orderDate;
  let max = rows[0]!.orderDate;
  for (const r of rows) {
    const v = BigInt(r.totalPaise);
    sum += v;
    if (largest === null || v > largest.paise) largest = { paise: v, vendor: r.vendorName };
    if (r.orderDate < min) min = r.orderDate;
    if (r.orderDate > max) max = r.orderDate;
  }
  return {
    sumPaise: sum,
    avgPaise: sum / BigInt(rows.length),
    largest,
    dateRange: min === max ? formatDate(min) : `${formatDate(min)} → ${formatDate(max)}`,
  };
}

function KpiTile({
  tone,
  icon,
  label,
  value,
  meta,
}: {
  tone: "sky" | "warn" | "ok" | "muted";
  icon: React.ReactNode;
  label: string;
  value: string;
  meta: string;
}) {
  const stripe =
    tone === "sky"
      ? "bg-sky-600"
      : tone === "warn"
        ? "bg-warn"
        : tone === "ok"
          ? "bg-ok"
          : "bg-line-2";
  const iconWrap =
    tone === "sky"
      ? "bg-sky-50 text-sky-700"
      : tone === "warn"
        ? "bg-warn-50 text-warn"
        : tone === "ok"
          ? "bg-ok-50 text-ok"
          : "bg-bg text-muted";
  return (
    <div className="relative overflow-hidden rounded-md border border-line bg-paper p-4 shadow-sm">
      <span className={`absolute inset-y-0 left-0 w-1 ${stripe}`} aria-hidden />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted">{label}</span>
        <span className={`grid h-7 w-7 place-items-center rounded-md ${iconWrap}`}>{icon}</span>
      </div>
      <div className="mt-1.5 text-xl font-bold tracking-tight text-ink">{value}</div>
      <div className="mt-0.5 text-xs text-muted">{meta}</div>
    </div>
  );
}
